<template>
  <div class="chat-page">
    <div class="chat-flex" :class="{ 'is-resizing': resizing }">
      <!-- ============ Left: Channels + Chat list ============ -->
      <div class="chat-col left-col" :style="{ width: leftWidth + 'px' }">
        <div class="left-stack d-flex flex-column">
          <div class="channel-area">
            <ChannelDropdown
              :shop-id="shopId"
              :active-id="activeChannel ? activeChannel.platformConnectionID : null"
              @select="onSelectChannel"
            />
          </div>
          <v-divider />
          <div class="title-area flex-grow-1">
            <ChatTitleList
              ref="titleList"
              :shop-id="shopId"
              :platform-connection-i-d="activeChannel ? activeChannel.platformConnectionID : null"
              :platform-i-d="activeChannel ? activeChannel.platformID : null"
              :active-chat-id="activeChat ? activeChat.chatID : null"
              :tags-by-chat="tagsByChat"
              :available-tags="availableTags"
              @select="onSelectChat"
            />
          </div>
        </div>
      </div>

      <!-- Splitter (Left ↔ Middle) -->
      <div
        class="splitter"
        :class="{ active: resizing === 'left' }"
        @mousedown="(e) => startResize(e, 'left')"
        :title="use_language.drag_to_resize"
      ></div>

      <!-- ============ Middle: Conversation ============ -->
      <div class="chat-col middle-col">
        <div class="d-flex flex-column" style="height: 100%">
          <div class="flex-grow-1" style="min-height: 0">
            <ChatConversation
              ref="conv"
              :shop-id="shopId"
              :staff-shop-id="staffShopId"
              :chat-id="activeChat ? activeChat.chatID : null"
              :platform-id="activeChat ? activeChat.platformID : null"
              :platform-connection-i-d="activeChannel ? activeChannel.platformConnectionID : null"
              :chat-title="activeChat"
            />
          </div>
          <ChatInputBar
            :disabled="!activeChat"
            :sending="sending"
            :shop-id="shopId"
            :staff-shop-id="staffShopId"
            @send="onSend"
            ref="input"
          />
        </div>
      </div>

      <!-- Splitter (Middle ↔ Right) -->
      <div
        class="splitter"
        :class="{ active: resizing === 'right' }"
        @mousedown="(e) => startResize(e, 'right')"
        :title="use_language.drag_to_resize"
      ></div>

      <!-- ============ Right: Tabs panel ============ -->
      <div class="chat-col right-col" :style="{ width: rightWidth + 'px' }">
        <!-- ทุก tab โชว์เสมอ (แค่ disabled tab "สร้างออเดอร์" ถ้าไม่รองรับ) — index คงที่ 0/1/2 -->
        <v-tabs v-model="rightTab" grow>
          <v-tab>
            <v-icon size="18" class="mr-1">mdi-tag-outline</v-icon>{{ use_language.tag }}
          </v-tab>
          <v-tab :disabled="!showOrderTab">
            <v-icon size="18" class="mr-1">mdi-cart-plus</v-icon>{{ use_language.create_order }}
          </v-tab>
          <v-tab>
            <v-icon size="18" class="mr-1">mdi-history</v-icon>{{ use_language.history }}
          </v-tab>
        </v-tabs>
        <v-divider />
        <!-- Lazy-mount: render เฉพาะ tab ที่ active เพื่อลด initial cost -->
        <div class="right-tabs-items">
          <keep-alive>
            <TagPanel
              v-if="rightTab === 0"
              key="tag"
              :shop-id="shopId"
              :staff-shop-id="staffShopId"
              :chat-id="activeChat ? activeChat.chatID : null"
              @change="onTagsChange"
            />
            <OrderPanel
              v-else-if="rightTab === 1"
              key="order"
              ref="orderPanel"
              :shop-id="shopId"
              :staff-id="staffId"
              :chat-id="activeChat ? activeChat.chatID : null"
              :platform-id="activeChat ? activeChat.platformID : null"
              :shop-customer-id="activeChat ? activeChat.shopCustomerID : null"
              :chat-title="activeChat"
              @created="onOrderCreated"
            />
            <HistoryPanel
              v-else-if="rightTab === 2"
              key="history"
              :shop-id="shopId"
              :shop-customer-id="activeChat ? activeChat.shopCustomerID : null"
            />
          </keep-alive>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import ChannelDropdown from "./chat/ChannelDropdown.vue";
import ChatTitleList from "./chat/ChatTitleList.vue";
import ChatConversation from "./chat/ChatConversation.vue";
import ChatInputBar from "./chat/ChatInputBar.vue";
import TagPanel from "./chat/panels/TagPanel.vue";
import OrderPanel from "./chat/panels/OrderPanel.vue";
import HistoryPanel from "./chat/panels/HistoryPanel.vue";
import { expressService_dotnet, generalService_dotnet, shopService_dotnet } from "@/website/global";
import { token_api } from "@/website/backend/token";
import { PLATFORM, SEND_API } from "./chat/constants";
import { get_languages } from '@/website/global_function'

export default {
  name: "ChatUnified",
  components: {
    ChannelDropdown,
    ChatTitleList,
    ChatConversation,
    ChatInputBar,
    TagPanel,
    OrderPanel,
    HistoryPanel,
  },
  data: () => ({
    use_language: {},
    shopId: null,
    staffId: null,
    staffShopId: null,
    staffShop: null, // จาก StaffShop/get-staffshop-only-by-staffid
    activeChannel: null, // { platformConnectionID, platformID, channel }
    activeChat: null,    // title object
    sending: false,
    rightTab: 0,
    connection: null,
    socketConnectionId: "",
    tagsByChat: {},
    availableTags: [],
    // ====== Resizable splitter state ======
    leftWidth: 300,      // px — ความกว้างคอลัมน์ซ้าย (channel + chat list)
    rightWidth: 380,     // px — ความกว้างคอลัมน์ขวา (tabs panel)
    resizing: null,      // 'left' | 'right' | null
  }),
  computed: {
    showOrderTab() {
      if (!this.activeChat) return false;
      const p = parseInt(this.activeChat.platformID);
      return p === PLATFORM.FACEBOOK || p === PLATFORM.LINE;
    },
  },
  watch: {
    // เรียก ensureMasterLoaded เฉพาะตอน user เปิด tab "สร้างออเดอร์" จริง
    rightTab(val) {
      if (val === 1 && this.showOrderTab) {
        this.$nextTick(() => {
          if (this.$refs.orderPanel && this.$refs.orderPanel.ensureMasterLoaded) {
            this.$refs.orderPanel.ensureMasterLoaded();
          }
        });
      }
    },
    // ถ้า user อยู่ tab "สร้างออเดอร์" แล้วสลับไป chat ที่ไม่รองรับ → fallback ไป Tab 0
    showOrderTab(val) {
      if (!val && this.rightTab === 1) this.rightTab = 0;
    },
  },
  async created() {
    this.shopId = parseInt(localStorage.getItem("shop_id"))
      || (JSON.parse(localStorage.getItem("shop") || "null") || {}).id
      || null;
    this.staffId = parseInt(localStorage.getItem("staff_id")) || null;
    this.use_language = await this.get_languages(localStorage.getItem("set_language"));
    await this._loadStaffShop();
    this.connectSocket();
    window.addEventListener("beforeunload", this._onBeforeUnload);
  },
  mounted() {
    // โหลดความกว้างที่เคย save ไว้
    try {
      const saved = localStorage.getItem("chat-unified-widths");
      if (saved) {
        const w = JSON.parse(saved);
        if (typeof w.left === "number" && w.left >= 200) this.leftWidth = w.left;
        if (typeof w.right === "number" && w.right >= 280) this.rightWidth = w.right;
      }
    } catch (_) {
      // ignore — ใช้ค่า default ถ้า parse/read ไม่ได้
    }
    // global listener สำหรับ drag (mousemove/mouseup เกิดข้างนอก splitter element ได้)
    window.addEventListener("mousemove", this._onResize);
    window.addEventListener("mouseup", this._endResize);
  },
  beforeDestroy() {
    this.closeSocket();
    window.removeEventListener("beforeunload", this._onBeforeUnload);
    window.removeEventListener("mousemove", this._onResize);
    window.removeEventListener("mouseup", this._endResize);
  },
  methods: {
    get_languages,
    async _loadStaffShop() {
      try {
        const res = await axios.post(
          shopService_dotnet + "StaffShop/get-staffshop-only-by-staffid",
          { id: this.staffId },
          { headers: { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("authorization_token") } }
        );
        this.staffShop = res.data && res.data[0] ? res.data[0] : res.data;
        if (this.staffShop) this.staffShopId = this.staffShop.id;
      } catch (e) {
        console.error("[load-staff-shop]", e);
      }
    },

    // ================= WebSocket =================
    connectSocket() {
      this.connection = new WebSocket(
        "wss://25nfmzc76e.execute-api.ap-southeast-1.amazonaws.com/production"
      );

      this.connection.onmessage = (event) => {
        // backend ส่ง "ConnectId<xxxx>" หรือ "ConnectId = xxxx" เป็น handshake ตอน connect ครั้งแรก
        // ตัด prefix "ConnectId" + separator (space, =, :, ,) ออกให้หมด เหลือเฉพาะ ID
        if (typeof event.data === "string" && event.data.startsWith("ConnectId")) {
          this.socketConnectionId = event.data.replace(/^ConnectId[\s=:,]*/, "").trim();
          this.registerSocket();
          return;
        }
        try {
          const msg = JSON.parse(event.data);
          this.getMessageFromSocket(msg);
        } catch (e) {
          console.warn("[WS] parse error:", e.message || e);
        }
      };

      this.connection.onerror = (err) => {
        console.warn("[WS] error:", err && err.message);
      };

      this.connection.onclose = () => {
        // auto-reconnect ใน 1 วินาที (โดน nullify ใน closeSocket ก่อนปิดเอง)
        setTimeout(() => this.connectSocket(), 1000);
      };
    },

    async registerSocket() {
      try {
        await axios.post(
          expressService_dotnet + "CoreMessage/update-connetionid",
          {
            shopId: this.shopId,
            staffShopId: this.staffShopId,
            connectionId: this.socketConnectionId,
            isOnline: true,
          },
          { headers: { "Content-Type": "application/json", Authorization: "Bearer " + token_api } }
        );
      } catch (e) {
        console.warn("[WS] register error:", e.message || e);
      }
    },

    getMessageFromSocket(msg) {
      // ===== New message =====
      if (msg.SocketType === "New") {
        // กรอง: เปิดเฉพาะ message ที่อยู่ใน channel ปัจจุบัน (หรือ "all" = connectionID 0)
        const sameChannel =
          !this.activeChannel ||
          this.activeChannel.platformConnectionID === 0 ||
          (msg.ChatTitle && this.activeChannel.platformConnectionID === msg.ChatTitle.PlatformConnectionID);
        if (!sameChannel) return;

        // update title list (ย้ายขึ้นบน + เพิ่ม unreadCount ถ้าไม่ใช่ chat ปัจจุบัน) — child จัดการเอง
        if (this.$refs.titleList) this.$refs.titleList.handleNewMessage(msg);

        // ถ้าเป็น message ของ chat ที่กำลังเปิดอยู่ → append + mark-read ทันที
        if (this.activeChat && parseInt(msg.ChatId) === parseInt(this.activeChat.chatID)) {
          const mapped = {
            is_admin_read: msg.sent_by_staffId != null,
            is_customer_read: true,
            loadImage: true,
            messageType: msg.MessageType,
            message: msg.Message,
            messageId: msg.MessageId,
            message_received_timestamp: msg.CreatedDatetime,
            message_sent_timestamp: msg.CreatedDatetime,
            sent_by_admin: msg.sent_by_staffId != null,
            sent_by_customer: msg.sent_by_staffId == null,
            sent_by_name: msg.SendBy,
            sent_by_staffId: msg.sent_by_staffId,
            sent_by_staffName: msg.sent_by_staffName,
            sent_time: msg.CreatedDatetime,
            read_by_staffId: this.staffShop ? this.staffShop.id : null,
            read_by_staffname: this.staffShop ? this.staffShop.name : "",
          };
          if (this.$refs.conv) {
            this.$refs.conv.appendMessage(mapped);
            this.$refs.conv.markRead(msg.MessageId);
          }
        }
      }

      // ===== Read receipt =====
      if (msg.SocketType === "Read") {
        if (this.$refs.titleList) this.$refs.titleList.handleMarkRead(msg);
      }
    },

    async closeSocket() {
      try {
        if (this.socketConnectionId) {
          await axios.post(
            expressService_dotnet + "CoreMessage/update-connetionid",
            {
              shopId: this.shopId,
              staffShopId: this.staffShopId,
              connectionId: this.socketConnectionId,
              isOnline: false,
            },
            { headers: { "Content-Type": "application/json", Authorization: "Bearer " + token_api } }
          );
        }
      } catch (e) {
        console.warn("[WS] closeSocket error:", e);
      }
      // ปิด reconnect loop ก่อน close — ไม่งั้น onclose จะ re-connect ทันที
      if (this.connection) {
        this.connection.onclose = null;
        this.connection.close();
        this.connection = null;
      }
    },

    _onBeforeUnload() {
      this.closeSocket();
    },

    onSelectChannel({ platformConnectionID, platformID, channel }) {
      this.activeChannel = { platformConnectionID, platformID, channel };
      this.activeChat = null;
    },

    async onSelectChat(item) {
      this.activeChat = item;
      // reset right-tab → tab แรก
      if (this.rightTab !== 0) this.rightTab = 0;
      // clear unread in list
      if (this.$refs.titleList) this.$refs.titleList.clearUnread(item.chatID);
    },

    onTagsChange({ tags, byChat }) {
      this.availableTags = tags;
      this.tagsByChat = byChat;
    },

    onOrderCreated() {
      // optional: ส่งข้อความ "สร้างออเดอร์ PAxxxx แล้ว" ในแชต (ปล่อยให้ user ตัดสินใจเอง)
    },

    // ================= Resizable Splitters =================
    startResize(e, which) {
      this.resizing = which;
      // เก็บค่าเริ่มต้นแบบ non-reactive (ใช้ instance property ตรงๆ)
      this._startX = e.clientX;
      this._startLeftWidth = this.leftWidth;
      this._startRightWidth = this.rightWidth;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      e.preventDefault();
    },
    _onResize(e) {
      if (!this.resizing) return;
      const delta = e.clientX - this._startX;
      if (this.resizing === "left") {
        this.leftWidth = Math.max(220, Math.min(500, this._startLeftWidth + delta));
      } else if (this.resizing === "right") {
        this.rightWidth = Math.max(280, Math.min(600, this._startRightWidth - delta));
      }
    },
    _endResize() {
      if (!this.resizing) return;
      this.resizing = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      // persist ลง localStorage หลังปล่อย
      try {
        localStorage.setItem(
          "chat-unified-widths",
          JSON.stringify({ left: this.leftWidth, right: this.rightWidth })
        );
      } catch (_) {
        // ignore — localStorage quota เต็ม / private mode
      }
    },

    // ============= ส่งข้อความ =============
    async onSend(payload) {
      if (!this.activeChat || this.sending) return;
      const p = parseInt(this.activeChat.platformID);
      this.sending = true;
      try {
        // ลำดับ: ส่งรูป → ส่งไฟล์ → ส่ง text
        // ทุก type ทำ optimistic append หลังส่งสำเร็จ (FB/LINE ไม่ส่ง webhook กลับมาให้ตัวเอง)
        let imageUrl = null, fileUrl = null;
        if (payload.image) imageUrl = await this._sendImage(p, payload.image);
        if (payload.file) fileUrl = await this._sendFile(p, payload.file);
        if (payload.text) await this._sendText(p, payload.text);

        if (this.$refs.conv) {
          if (imageUrl) this.$refs.conv.appendMessage(this._buildOptimisticMessage("image", imageUrl));
          if (fileUrl) this.$refs.conv.appendMessage(this._buildOptimisticMessage("file", fileUrl));
          if (payload.text) this.$refs.conv.appendMessage(this._buildOptimisticMessage("text", payload.text));
        }

        if (this.$refs.input) this.$refs.input.clearInput();
      } catch (err) {
        this.$swal && this.$swal({ icon: "error", title: this.use_language.send_message_failed, text: err.message || this.use_language.error });
      } finally {
        this.sending = false;
      }
    },

    _buildOptimisticMessage(messageType, content) {
      return {
        is_admin_read: true,
        is_customer_read: false,
        loadImage: true,             // กัน _fetchLineMediaForRecent fetch ซ้ำสำหรับรูปที่เรามี URL แล้ว
        messageType,
        message: content,
        messageId: "",
        sent_by_admin: true,
        sent_by_customer: false,
        sent_by_name: this.staffShop ? this.staffShop.name : "",
        sent_by_staffId: this.staffShop ? this.staffShop.id : null,
        sent_by_staffName: this.staffShop ? this.staffShop.name : "",
        sent_time: new Date().toISOString(),
        read_by_staffId: "",
        read_by_staffname: "",
      };
    },

    async _sendText(platformId, text) {
      return this._sendByType(platformId, text, "text");
    },

    // type = "text" | "image" | "file"
    async _sendByType(platformId, payload, type) {
      const endpoint = SEND_API[platformId];
      if (!endpoint) throw new Error("Platform " + this.use_language.not_supported);
      const headers = { "Content-Type": "application/json", Authorization: "Bearer " + token_api };
      const baseBody = {
        shopId: this.shopId,
        chatId: parseInt(this.activeChat.chatID),
        staffShopId: this.staffShopId,
      };
      let body;
      if (platformId === PLATFORM.LINE) {
        body = {
          ...baseBody,
          messages: payload,
          messageType: type,
          replyToken: this.activeChat.reply_token || "",
        };
      } else if (platformId === PLATFORM.SHOPEE) {
        // Shopee API ปัจจุบันรับเฉพาะ text — ถ้า image ให้ส่ง URL เป็น text ก่อน (TODO: ปรับเป็น image type)
        body = { ...baseBody, messageType: "text", content: { text: payload } };
      } else {
        // Facebook + others
        body = { ...baseBody, message: payload, messageType: type };
      }
      const res = await axios.post(expressService_dotnet + endpoint, body, { headers });
      if (res.data && res.data.status && res.data.status !== "success") {
        throw new Error(res.data.message || this.use_language.send_message_failed);
      }
    },

    async _uploadFile(file) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("bucket", "chat-attachment");
      const res = await axios.post(
        generalService_dotnet + "General/upload-file-with-bucket",
        fd,
        { headers: { "Content-Type": "multipart/form-data", Authorization: "Bearer " + localStorage.getItem("authorization_token") } }
      );
      // backend คืนเป็น file_path (รองรับ url เผื่อ legacy)
      return (res.data && (res.data.file_path || res.data.url)) || "";
    },

    async _sendImage(platformId, file) {
      const url = await this._uploadFile(file);
      if (!url) throw new Error("Upload " + this.use_language.image_failed);
      // ส่ง URL พร้อม messageType=image → backend จะ format เป็น attachment ของแต่ละ platform
      await this._sendByType(platformId, url, "image");
      return url;   // ใช้ใน onSend สำหรับ optimistic append
    },

    async _sendFile(platformId, file) {
      const url = await this._uploadFile(file);
      if (!url) throw new Error("Upload " + this.use_language.file_failed);
      // ส่งเป็น "file" attachment (FB รองรับ, LINE ใช้ image-only ตอนนี้)
      await this._sendByType(platformId, url, "file");
      return url;
    },
  },
};
</script>

<style scoped>
.chat-page {
  height: calc(100vh - 100px);
  overflow: hidden;
  background: #f5f5f5;
}
.chat-flex {
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
}
/* ระหว่าง drag — ปิด pointer events บน iframe/children เพื่อไม่ให้กิน mouse event */
.chat-flex.is-resizing iframe,
.chat-flex.is-resizing .chat-col {
  pointer-events: none;
}
.chat-flex.is-resizing .splitter {
  pointer-events: auto;
}

.chat-col {
  height: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.left-col,
.right-col {
  flex-shrink: 0;       /* ขนาดคงที่ตาม leftWidth/rightWidth */
}
.middle-col {
  flex: 1 1 0;           /* ขยายเต็มที่เหลือ */
  min-width: 320px;
  background: #fafafa;
}
.left-stack {
  height: 100%;
}
.channel-area {
  flex: 0 0 auto;
}
.title-area {
  min-height: 0;
  overflow: hidden;
}
.right-tabs-items {
  flex: 1 1 auto;
  overflow-y: auto;
  height: calc(100% - 48px);
}

/* ===== Splitter (draggable divider) ===== */
.splitter {
  width: 4px;
  flex-shrink: 0;
  background: #e0e0e0;
  cursor: col-resize;
  position: relative;
  transition: background 0.15s;
}
.splitter::before {
  /* zone กว้างขึ้นเพื่อจับง่าย (visual ยังคงเป็น 4px) */
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: -3px;
  right: -3px;
}
.splitter:hover,
.splitter.active {
  background: #1976d2;
}

/* ===== Mobile fallback: stack vertically ===== */
@media (max-width: 960px) {
  .chat-page {
    height: auto;
  }
  .chat-flex {
    flex-direction: column;
  }
  .chat-col {
    width: 100% !important;
    border-bottom: 1px solid #e0e0e0;
    min-height: 400px;
  }
  .splitter {
    display: none;
  }
}
</style>
